import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, useLocation } from 'react-router';
import { AppUpdateBar, reloadOnMissingCode, useAppUpdate } from '@crave/ui';
import '@crave/ui/tokens.css';
import './styles/admin.css';
import { App } from './App.tsx';
import { ResetPasswordPage } from './pages/ResetPasswordPage.tsx';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 5_000 } },
});

if (import.meta.env.PROD) reloadOnMissingCode();

/** A newer deploy shows a slim bar, and the admin reloads itself on the next page change. */
function UpdateWatcher() {
  const { pathname, search } = useLocation();
  const { action, reload } = useAppUpdate({
    policy: 'admin',
    currentVersion: import.meta.env['VITE_BUILD_ID'] as string | undefined,
    enabled: import.meta.env.PROD,
    path: pathname,
    search,
    // Let a save that is still running finish before reloading.
    isBusy: () => queryClient.isMutating() > 0,
  });
  return action === 'show-bar' ? <AppUpdateBar onReload={reload} /> : null;
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        {window.location.pathname.startsWith('/reset-password') ? <ResetPasswordPage /> : <App />}
        <UpdateWatcher />
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
